/**
 * DecisionFlow — TransactionPlanManager
 * ui/src/components/tenant-admin/TransactionPlanManager.jsx
 * Stage 8A — Transaction Crediting Domain (§44)
 *
 * Four tabs:
 *   8A-1  transaction_plan_definition  — create / edit / submit / approve plan
 *   8A-2  transaction_rate_matrix      — add rate rows to approved plan
 *   8A-3  transaction_assignment       — assign plan to transaction-mode positions
 *   8A-4  transaction_validation       — coverage summary / import upload / confirm
 *
 * API contract: docs/api_contract_v1_31.md §44
 * Spec: docs/decisionflow_transaction_crediting_spec_v1_0.md §3 + §4
 *
 * S308 — OI-S307-UI-THEME: Full CSS token compliance pass.
 *   All hardcoded Tailwind color classes replaced with rgb(var(--color-*)) tokens.
 *   All text-sm/xs/base/lg replaced with text-tok-sm/xs/base/lg.
 *   Shared class strings extracted (thClass, tdClass, btnPrimary, btnSecondary,
 *   inputClass, labelClass) matching PlanAssignmentManager reference pattern.
 * S308 — OI-S307-APPROVAL-PANEL: ApprovalPanel prop names corrected.
 *   variant="plan" (not "transaction_plan" — not in VARIANT_CONFIG).
 *   currentState= (not currentStatus=).
 *   submittedBy={null} / currentUserId={user?.user_id} passed correctly.
 *   Removed non-existent props: entityId, requireApprovalNote.
 *
 * Rules (unchanged):
 *  - apiGet / apiPost from client — never raw fetch except PATCH + multipart
 *  - plan_status NOT a real column — derive: approved_by IS NOT NULL → 'approved', else → 'draft'
 *  - submitted_at / submitted_by NOT real columns — pass null to ApprovalPanel
 *  - pending_approval tracked in local React state only (optimistic after submit)
 *  - Reject endpoint NOT confirmed — onReject={null}
 *  - Feature gate: 403 FEATURE_UNAVAILABLE → GateStatusBanner
 *  - tenant_super_admin excluded (NN-36)
 *  - Only transaction / hybrid_transaction positions eligible for assignment
 *
 * DDL ground truth (schema/ddl — overrides spec):
 *   transaction_commission_plan: plan_id, plan_name, plan_description,
 *     effective_from, effective_to, rate_resolution_order, is_active,
 *     approved_by, approved_at, created_by, created_at, updated_at
 *     NO: plan_code, plan_version, plan_status, submitted_at, submitted_by
 *   transaction_commission_rate: rate_id, plan_id, product_family_id (int FK),
 *     product_line, selling_motion_id, territory_code,
 *     min_deal_amount, max_deal_amount, commission_rate_pct,
 *     effective_from, effective_to, is_active, created_by, created_at, updated_at
 *     NO: role_id, product_family_code, commission_rate,
 *         minimum_transaction_amount, maximum_transaction_amount
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../auth/AuthContext.jsx";
import { useFeatures } from "../../auth/FeatureContext.jsx";
import { apiGet, apiPost } from "../../api/client";
import ApprovalPanel from "../shared/ApprovalPanel.jsx";
import GateStatusBanner from "../shared/GateStatusBanner.jsx";

// ─── shared class strings (token-compliant — matches PlanAssignmentManager) ──

const thClass =
  "px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide " +
  "text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] " +
  "border-b border-[rgb(var(--color-border))]";

const tdClass =
  "px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] " +
  "border-b border-[rgb(var(--color-border))]";

const btnPrimary =
  "rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium " +
  "text-white hover:bg-[rgb(var(--color-primary-hover))] " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const btnSecondary =
  "rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] " +
  "px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] " +
  "hover:bg-[rgb(var(--color-surface-muted))] " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const inputClass =
  "mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] " +
  "bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] " +
  "px-3 py-2 text-tok-sm focus:outline-none " +
  "focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]";

const labelClass = "flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]";

// ─── helpers ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: "plan",       label: "Transaction Plan",   substage: "8A-1" },
  { key: "rates",      label: "Rate Matrix",         substage: "8A-2" },
  { key: "assignment", label: "Position Assignment", substage: "8A-3" },
  { key: "validation", label: "Validation",          substage: "8A-4" },
];

const BASIS_OPTIONS = ["revenue", "margin", "units", "fixed_amount"];

/**
 * Derive plan status from actual DDL columns.
 * plan_status is NOT a real column — approved_by IS NOT NULL means approved.
 * pending_approval is local React state only (optimistic after submit).
 */
function derivePlanStatus(plan) {
  if (plan._localStatus) return plan._localStatus;
  if (plan.approved_by) return "approved";
  return "draft";
}

function fmt(val) {
  if (val === null || val === undefined) return "—";
  return val;
}

function fmtDate(val) {
  if (!val) return "—";
  return val.slice(0, 10);
}

/** Token-compliant status pill — replaces hardcoded color version */
function StatusPill({ status }) {
  const map = {
    draft: "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]",
    pending_approval: "bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]",
    approved: "bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]",
    superseded: "bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${
        map[status] ?? "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
      }`}
    >
      {status ?? "—"}
    </span>
  );
}

// ─── 8A-1 — Plan Definition ──────────────────────────────────────────────────

function PlanDefinitionTab({ fiscalPeriodId, userId, planId }) {
  const [plans, setPlans]       = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView]         = useState("list"); // list | create | detail | edit
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [gated, setGated]       = useState(false);

  const [form, setForm] = useState({
    plan_name: "",
    plan_description: "",
    calculation_basis: "revenue",
    effective_from: "",
    effective_to: "",
  });
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // C7 — S689 plan_id scoping: scope transaction plans to this plan when planId prop injected.
      const txParams = new URLSearchParams();
      if (planId != null) txParams.set('plan_id', String(planId));
      if (fiscalPeriodId) txParams.set('fiscal_period_id', String(fiscalPeriodId));
      const res = await apiGet(
        `/api/v1/transaction-plans${txParams.toString() ? '?' + txParams.toString() : ''}`
      );
      setPlans(res.data ?? []);
    } catch (e) {
      if (e.status === 403) { setGated(true); return; }
      setError(e.message ?? "Failed to load transaction plans.");
    } finally {
      setLoading(false);
    }
  }, [fiscalPeriodId, planId]);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        plan_name: form.plan_name,
        plan_description: form.plan_description || null,
        calculation_basis: form.calculation_basis,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      };
      await apiPost("/api/v1/transaction-plans", payload);
      setView("list");
      setForm({ plan_name: "", plan_description: "", calculation_basis: "revenue", effective_from: "", effective_to: "" });
      await load();
    } catch (e) {
      setFormError(e.message ?? "Failed to create plan.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        plan_name: form.plan_name,
        plan_description: form.plan_description || null,
        calculation_basis: form.calculation_basis,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      };
      // PATCH — raw fetch, no apiPatch export
      const res = await fetch(`/api/v1/transaction-plans/${selected.plan_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `Update failed (${res.status})`);
      }
      setView("detail");
      await load();
      // Refresh selected from updated list
      setSelected(prev => ({ ...prev, ...payload }));
    } catch (e) {
      setFormError(e.message ?? "Failed to update plan.");
    } finally {
      setSaving(false);
    }
  }

  function openDetail(plan) {
    setSelected(plan);
    setForm({
      plan_name: plan.plan_name,
      plan_description: plan.plan_description ?? "",
      calculation_basis: plan.calculation_basis ?? "revenue",
      effective_from: plan.effective_from?.slice(0, 10) ?? "",
      effective_to: plan.effective_to?.slice(0, 10) ?? "",
    });
    setView("detail");
  }

  if (gated) return <GateStatusBanner code="TRANSACTION_FEATURE_UNAVAILABLE" />;

  const selectedStatus = selected ? derivePlanStatus(selected) : null;

  // ── create / edit form ───────────────────────────────────────────────────
  if (view === "create" || view === "edit") {
    const isEdit = view === "edit";
    return (
      <div className="max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => setView(isEdit ? "detail" : "list")}
            className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
          >
            ← Back
          </button>
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
            {isEdit ? "Edit Transaction Plan" : "Create Transaction Plan"}
          </h3>
        </div>

        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm">
          <form onSubmit={isEdit ? handleUpdate : handleCreate} className="flex flex-col gap-4">
            <label className={labelClass}>
              Plan Name <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                className={inputClass}
                value={form.plan_name}
                onChange={e => setForm(f => ({ ...f, plan_name: e.target.value }))}
                required
                placeholder="e.g. Enterprise Hardware Transaction Plan"
              />
            </label>

            <label className={labelClass}>
              Description
              <textarea
                className={inputClass + " min-h-16 resize-y"}
                value={form.plan_description}
                onChange={e => setForm(f => ({ ...f, plan_description: e.target.value }))}
                rows={2}
                placeholder="Optional description"
              />
            </label>

            <label className={labelClass}>
              Calculation Basis <span className="text-[rgb(var(--color-danger))]">*</span>
              <select
                className={inputClass}
                value={form.calculation_basis}
                onChange={e => setForm(f => ({ ...f, calculation_basis: e.target.value }))}
                required
              >
                {BASIS_OPTIONS.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className={labelClass}>
                Effective From <span className="text-[rgb(var(--color-danger))]">*</span>
                <input
                  type="date"
                  className={inputClass}
                  value={form.effective_from}
                  onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
                  required
                />
              </label>
              <label className={labelClass}>
                Effective To
                <input
                  type="date"
                  className={inputClass}
                  value={form.effective_to}
                  onChange={e => setForm(f => ({ ...f, effective_to: e.target.value }))}
                />
              </label>
            </div>

            {formError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {formError}
              </div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button type="submit" disabled={saving} className={btnPrimary}>
                {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Plan"}
              </button>
              <button
                type="button"
                onClick={() => setView(isEdit ? "detail" : "list")}
                className={btnSecondary}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── detail view ──────────────────────────────────────────────────────────
  if (view === "detail" && selected) {
    return (
      <div className="max-w-2xl flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => { setView("list"); setSelected(null); }}
            className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
          >
            ← Back
          </button>
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
            {selected.plan_name}
          </h3>
          <StatusPill status={selectedStatus} />
          {selectedStatus === "draft" && (
            <button
              type="button"
              onClick={() => setView("edit")}
              className={btnSecondary + " ml-auto"}
            >
              Edit
            </button>
          )}
        </div>

        {/* Plan detail fields */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Plan Name</p>
              <p className="mt-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">{fmt(selected.plan_name)}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Calculation Basis</p>
              <p className="mt-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">{fmt(selected.calculation_basis)}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Effective From</p>
              <p className="mt-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">{fmtDate(selected.effective_from)}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Effective To</p>
              <p className="mt-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">{fmtDate(selected.effective_to)}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Description</p>
              <p className="mt-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">{fmt(selected.plan_description)}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Created</p>
              <p className="mt-1 text-tok-sm font-medium text-[rgb(var(--color-text))]">{fmtDate(selected.created_at)}</p>
            </div>
          </div>
        </div>

        {/*
          ApprovalPanel — OI-S307-APPROVAL-PANEL fix:
          - variant="plan" (VARIANT_CONFIG has no "transaction_plan" key)
          - currentState= (not currentStatus=)
          - currentUserId from user prop (needed for self-approval block)
          - submittedBy={null} — submitted_by does NOT exist on Phase 2 tables
          - approvedBy / approvedAt from real DDL columns
          - onReject={null} — reject endpoint not confirmed for §44
          - Removed: entityId, requireApprovalNote (not real ApprovalPanel props)
        */}
        <ApprovalPanel
          variant="plan"
          currentState={selectedStatus}
          currentUserId={userId}
          submittedBy={null}
          approvedBy={selected.approved_by ?? null}
          approvedAt={selected.approved_at ?? null}
          onSubmit={async () => {
            await apiPost(`/api/v1/transaction-plans/${selected.plan_id}/submit`, {});
            // pending_approval is local state only — no submitted_at column on Phase 2
            setSelected(prev => ({ ...prev, _localStatus: "pending_approval" }));
            await load();
          }}
          onApprove={async () => {
            await apiPost(`/api/v1/transaction-plans/${selected.plan_id}/approve`, {});
            setSelected(prev => ({ ...prev, _localStatus: "approved" }));
            await load();
          }}
          onReject={null}
        />
      </div>
    );
  }

  // ── list view ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Transaction Plans</h3>
        <button type="button" onClick={() => setView("create")} className={btnPrimary}>
          + New Plan
        </button>
      </div>

      {loading && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
      )}
      {error && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error}
        </div>
      )}
      {!loading && !error && plans.length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
          No transaction plans yet. Create one to get started.
        </p>
      )}

      {plans.length > 0 && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
          <table className="min-w-full text-tok-sm border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Plan Name</th>
                <th className={thClass}>Basis</th>
                <th className={thClass}>Effective From</th>
                <th className={thClass}>Status</th>
                <th className={thClass} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.plan_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                  <td className={tdClass}>{p.plan_name}</td>
                  <td className={tdClass}>{p.calculation_basis}</td>
                  <td className={tdClass}>{fmtDate(p.effective_from)}</td>
                  <td className={tdClass}><StatusPill status={derivePlanStatus(p)} /></td>
                  <td className={tdClass}>
                    <button
                      type="button"
                      onClick={() => openDetail(p)}
                      className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── 8A-2 — Rate Matrix ───────────────────────────────────────────────────────

function RateMatrixTab() {
  const [plans, setPlans]               = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [rates, setRates]               = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingRates, setLoadingRates] = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [gated, setGated]               = useState(false);
  const [error, setError]               = useState(null);
  const [formError, setFormError]       = useState(null);
  const [saving, setSaving]             = useState(false);

  const [form, setForm] = useState({
    product_line: "",
    product_family_id: "",
    selling_motion_id: "",
    territory_code: "",
    commission_rate_pct: "",
    min_deal_amount: "0.00",
    max_deal_amount: "",
    effective_from: "",
    effective_to: "",
  });

  useEffect(() => {
    async function loadPlans() {
      setLoadingPlans(true);
      try {
        const res = await apiGet("/api/v1/transaction-plans");
        setPlans(res.data ?? []);
      } catch (e) {
        if (e.status === 403) { setGated(true); return; }
        setError(e.message ?? "Failed to load plans.");
      } finally {
        setLoadingPlans(false);
      }
    }
    loadPlans();
  }, []);

  async function loadRates(id) {
    setLoadingRates(true);
    setError(null);
    try {
      const res = await apiGet(`/api/v1/transaction-rates?plan_id=${id}`);
      setRates(res.data ?? []);
    } catch (e) {
      setError(e.message ?? "Failed to load rates.");
    } finally {
      setLoadingRates(false);
    }
  }

  function handlePlanChange(e) {
    const id = e.target.value;
    setSelectedPlanId(id);
    if (id) loadRates(id);
    else setRates([]);
  }

  async function handleAddRate(e) {
    e.preventDefault();
    if (form.product_line && form.product_family_id) {
      setFormError("Cannot specify both product_line and product_family_id.");
      return;
    }
    if (!form.product_line && !form.product_family_id) {
      setFormError("At least one discriminator required: product_line or product_family_id.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        plan_id: parseInt(selectedPlanId),
        product_line: form.product_line || null,
        product_family_id: form.product_family_id ? parseInt(form.product_family_id) : null,
        selling_motion_id: form.selling_motion_id ? parseInt(form.selling_motion_id) : null,
        territory_code: form.territory_code || null,
        commission_rate_pct: form.commission_rate_pct,
        min_deal_amount: form.min_deal_amount || "0.00",
        max_deal_amount: form.max_deal_amount || null,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      };
      await apiPost("/api/v1/transaction-rates", payload);
      setShowForm(false);
      setForm({
        product_line: "", product_family_id: "", selling_motion_id: "", territory_code: "",
        commission_rate_pct: "", min_deal_amount: "0.00", max_deal_amount: "",
        effective_from: "", effective_to: "",
      });
      await loadRates(selectedPlanId);
    } catch (e) {
      setFormError(e.message ?? "Failed to add rate.");
    } finally {
      setSaving(false);
    }
  }

  if (gated) return <GateStatusBanner code="TRANSACTION_FEATURE_UNAVAILABLE" />;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm">
        <label className={labelClass}>
          Select Plan
          {loadingPlans ? (
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading plans…</p>
          ) : (
            <select className={inputClass + " max-w-xs mt-1"} value={selectedPlanId} onChange={handlePlanChange}>
              <option value="">— choose a plan —</option>
              {plans.map(p => (
                <option key={p.plan_id} value={p.plan_id}>
                  {p.plan_name} ({derivePlanStatus(p)})
                </option>
              ))}
            </select>
          )}
        </label>
      </div>

      {selectedPlanId && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Rate Rows</h3>
            <button type="button" onClick={() => setShowForm(f => !f)} className={btnPrimary}>
              {showForm ? "Cancel" : "+ Add Rate"}
            </button>
          </div>

          {showForm && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm">
              <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))] mb-1">New Rate Row</h4>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-4">
                Specify product_line OR product_family_id — not both.
              </p>
              <form onSubmit={handleAddRate} className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <label className={labelClass}>
                    Product Line
                    <input
                      className={inputClass}
                      value={form.product_line}
                      onChange={e => setForm(f => ({ ...f, product_line: e.target.value }))}
                      placeholder="e.g. SERVERS"
                    />
                  </label>
                  <label className={labelClass}>
                    Product Family ID
                    <input
                      type="number"
                      className={inputClass}
                      value={form.product_family_id}
                      onChange={e => setForm(f => ({ ...f, product_family_id: e.target.value }))}
                      placeholder="e.g. 12"
                    />
                  </label>
                  <label className={labelClass}>
                    Selling Motion ID
                    <input
                      type="number"
                      className={inputClass}
                      value={form.selling_motion_id}
                      onChange={e => setForm(f => ({ ...f, selling_motion_id: e.target.value }))}
                      placeholder="e.g. 2"
                    />
                  </label>
                  <label className={labelClass}>
                    Territory Code
                    <input
                      className={inputClass}
                      value={form.territory_code}
                      onChange={e => setForm(f => ({ ...f, territory_code: e.target.value }))}
                      placeholder="e.g. AMER"
                    />
                  </label>
                  <label className={labelClass}>
                    Commission Rate % <span className="text-[rgb(var(--color-danger))]">*</span>
                    <input
                      className={inputClass}
                      value={form.commission_rate_pct}
                      onChange={e => setForm(f => ({ ...f, commission_rate_pct: e.target.value }))}
                      placeholder="e.g. 0.035000"
                      required
                    />
                  </label>
                  <label className={labelClass}>
                    Min Deal Amount
                    <input
                      className={inputClass}
                      value={form.min_deal_amount}
                      onChange={e => setForm(f => ({ ...f, min_deal_amount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </label>
                  <label className={labelClass}>
                    Max Deal Amount
                    <input
                      className={inputClass}
                      value={form.max_deal_amount}
                      onChange={e => setForm(f => ({ ...f, max_deal_amount: e.target.value }))}
                      placeholder="e.g. 250000.00"
                    />
                  </label>
                  <label className={labelClass}>
                    Effective From <span className="text-[rgb(var(--color-danger))]">*</span>
                    <input
                      type="date"
                      className={inputClass}
                      value={form.effective_from}
                      onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))}
                      required
                    />
                  </label>
                  <label className={labelClass}>
                    Effective To
                    <input
                      type="date"
                      className={inputClass}
                      value={form.effective_to}
                      onChange={e => setForm(f => ({ ...f, effective_to: e.target.value }))}
                    />
                  </label>
                </div>

                {formError && (
                  <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                    {formError}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button type="submit" disabled={saving} className={btnPrimary}>
                    {saving ? "Saving…" : "Add Rate Row"}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className={btnSecondary}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {error}
            </div>
          )}
          {loadingRates && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading rates…</p>
          )}
          {!loadingRates && rates.length === 0 && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
              No rate rows yet. At least one required before plan can be submitted.
            </p>
          )}

          {rates.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Product Line</th>
                    <th className={thClass}>Family ID</th>
                    <th className={thClass}>Motion</th>
                    <th className={thClass}>Territory</th>
                    <th className={thClass}>Rate %</th>
                    <th className={thClass}>Min Amt</th>
                    <th className={thClass}>Max Amt</th>
                    <th className={thClass}>Effective From</th>
                    <th className={thClass}>Effective To</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map(r => (
                    <tr key={r.rate_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass}>{fmt(r.product_line)}</td>
                      <td className={tdClass}>{fmt(r.product_family_id)}</td>
                      <td className={tdClass}>{fmt(r.selling_motion_id)}</td>
                      <td className={tdClass}>{fmt(r.territory_code)}</td>
                      <td className={tdClass + " font-mono text-tok-xs"}>{fmt(r.commission_rate_pct)}</td>
                      <td className={tdClass + " font-mono text-tok-xs"}>{fmt(r.min_deal_amount)}</td>
                      <td className={tdClass + " font-mono text-tok-xs"}>{fmt(r.max_deal_amount)}</td>
                      <td className={tdClass}>{fmtDate(r.effective_from)}</td>
                      <td className={tdClass}>{fmtDate(r.effective_to)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── 8A-3 — Position Assignment ───────────────────────────────────────────────

function PositionAssignmentTab() {
  const [plans, setPlans]             = useState([]);
  const [positions, setPositions]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [gated, setGated]             = useState(false);
  const [saving, setSaving]           = useState(null); // position_id being saved
  const [saveError, setSaveError]     = useState(null);
  const [assignments, setAssignments] = useState({}); // position_id → plan_id

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [planRes, posRes] = await Promise.all([
          apiGet("/api/v1/transaction-plans"),
          apiGet("/api/v1/positions"),
        ]);
        // Only approved plans eligible for assignment
        const approvedPlans = (planRes.data ?? []).filter(p => p.approved_by);
        setPlans(approvedPlans);
        // Only transaction / hybrid_transaction positions
        const txPositions = (posRes.data ?? []).filter(p =>
          p.calculation_mode === "transaction" || p.calculation_mode === "hybrid_transaction"
        );
        setPositions(txPositions);
        // Seed local assignment state from existing config
        const init = {};
        txPositions.forEach(p => {
          if (p.transaction_plan_id) init[p.position_id] = p.transaction_plan_id;
        });
        setAssignments(init);
      } catch (e) {
        if (e.status === 403) { setGated(true); return; }
        setError(e.message ?? "Failed to load data.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleAssign(positionId, planId) {
    setSaving(positionId);
    setSaveError(null);
    try {
      // PATCH — raw fetch, no apiPatch export
      const res = await fetch(`/api/v1/positions/${positionId}/calculation-config`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transaction_plan_id: planId ? parseInt(planId) : null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `Assignment failed (${res.status})`);
      }
      setAssignments(prev => ({ ...prev, [positionId]: planId }));
    } catch (e) {
      setSaveError(e.message ?? "Failed to save assignment.");
    } finally {
      setSaving(null);
    }
  }

  if (gated) return <GateStatusBanner code="TRANSACTION_FEATURE_UNAVAILABLE" />;

  if (loading) {
    return <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>;
  }

  if (error) {
    return (
      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
        {error}
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        No positions with calculation_mode of{" "}
        <code className="bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] px-1 rounded">
          transaction
        </code>{" "}
        or{" "}
        <code className="bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] px-1 rounded">
          hybrid_transaction
        </code>{" "}
        found. Transaction substages are not applicable for this tenant.
      </p>
    );
  }

  if (plans.length === 0) {
    return (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        No approved transaction plans available. Approve a plan in the Transaction Plan tab first.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        Assign an approved transaction plan to each eligible position. Only positions with{" "}
        <code className="bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] px-1 rounded">
          transaction
        </code>{" "}
        or{" "}
        <code className="bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] px-1 rounded">
          hybrid_transaction
        </code>{" "}
        calculation mode are shown.
      </p>

      {saveError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {saveError}
        </div>
      )}

      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
        <table className="min-w-full text-tok-sm border-collapse">
          <thead>
            <tr>
              <th className={thClass}>Position</th>
              <th className={thClass}>Calc Mode</th>
              <th className={thClass}>Assigned Plan</th>
              <th className={thClass}>Action</th>
            </tr>
          </thead>
          <tbody>
            {positions.map(pos => {
              const currentPlanId = assignments[pos.position_id] ?? "";
              const isSaving = saving === pos.position_id;
              return (
                <tr key={pos.position_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                  <td className={tdClass}>{pos.position_name ?? pos.position_id}</td>
                  <td className={tdClass}>
                    <span className="text-tok-xs font-mono bg-[rgb(var(--color-selected-bg))] text-[rgb(var(--color-primary))] px-1.5 py-0.5 rounded">
                      {pos.calculation_mode}
                    </span>
                  </td>
                  <td className={tdClass}>
                    <select
                      className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-sm focus:outline-none"
                      value={currentPlanId}
                      onChange={e => setAssignments(prev => ({ ...prev, [pos.position_id]: e.target.value }))}
                      disabled={isSaving}
                    >
                      <option value="">— none —</option>
                      {plans.map(p => (
                        <option key={p.plan_id} value={p.plan_id}>{p.plan_name}</option>
                      ))}
                    </select>
                  </td>
                  <td className={tdClass}>
                    <button
                      type="button"
                      onClick={() => handleAssign(pos.position_id, assignments[pos.position_id])}
                      disabled={isSaving || !assignments[pos.position_id]}
                      className={btnPrimary}
                    >
                      {isSaving ? "Saving…" : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── 8A-4 — Validation (import upload + coverage summary) ────────────────────

function ValidationTab({ fiscalPeriodId: initialFiscalPeriodId }) {
  const [importLog, setImportLog]           = useState(null);
  const [validation, setValidation]         = useState(null);
  const [uploading, setUploading]           = useState(false);
  const [confirming, setConfirming]         = useState(false);
  const [loadingVal, setLoadingVal]         = useState(false);
  const [fiscalPeriodId, setFiscalPeriodId] = useState(initialFiscalPeriodId ?? "");
  const [file, setFile]                     = useState(null);
  const [error, setError]                   = useState(null);
  const [gated, setGated]                   = useState(false);
  const [confirmError, setConfirmError]     = useState(null);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  async function handleUpload() {
    if (!file) { setError("Select a CSV file first."); return; }
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Raw fetch — multipart/form-data; browser sets Content-Type + boundary
      const res = await fetch("/api/v1/transaction-imports/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) {
        if (res.status === 403) { setGated(true); return; }
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setImportLog(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  async function fetchValidation() {
    if (!importLog?.import_log_id) return;
    setLoadingVal(true);
    setError(null);
    try {
      const res = await apiGet(`/api/v1/transaction-imports/${importLog.import_log_id}/validation`);
      setValidation(res);
    } catch (e) {
      setError(e.message ?? "Failed to fetch validation results.");
    } finally {
      setLoadingVal(false);
    }
  }

  async function handleConfirm() {
    if (!fiscalPeriodId) { setConfirmError("Fiscal period ID required."); return; }
    setConfirming(true);
    setConfirmError(null);
    try {
      const res = await apiPost(`/api/v1/transaction-imports/${importLog.import_log_id}/confirm`, {
        fiscal_period_id: parseInt(fiscalPeriodId),
        acknowledge_warnings: warningsAcknowledged,
      });
      setImportLog(prev => ({ ...prev, load_status: res.load_status }));
      setValidation(prev => prev ? { ...prev, coverage_summary: res.coverage_summary } : prev);
    } catch (e) {
      setConfirmError(e.message ?? "Failed to confirm import.");
    } finally {
      setConfirming(false);
    }
  }

  if (gated) return <GateStatusBanner code="TRANSACTION_FEATURE_UNAVAILABLE" />;

  return (
    <div className="max-w-2xl flex flex-col gap-6">

      {/* Upload */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm flex flex-col gap-4">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
          Upload Transaction Import CSV
        </h3>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".csv"
            onChange={e => setFile(e.target.files[0] ?? null)}
            className="text-tok-sm text-[rgb(var(--color-text-muted))]"
          />
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading || !file}
            className={btnPrimary}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
        {error && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {error}
          </div>
        )}
      </div>

      {/* Import status */}
      {importLog && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm flex flex-col gap-3">
          <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Import Status</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Import Log ID</p>
              <p className="mt-1 text-tok-xs font-mono text-[rgb(var(--color-text))]">{importLog.import_log_id}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Status</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{importLog.load_status}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Rows Received</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{importLog.rows_received?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Rows Loaded</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{importLog.rows_loaded?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Rows Rejected</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{importLog.rows_rejected?.toLocaleString()}</p>
            </div>
          </div>
          {importLog.load_status === "validating" && (
            <button
              type="button"
              onClick={fetchValidation}
              disabled={loadingVal}
              className={btnSecondary + " self-start mt-1"}
            >
              {loadingVal ? "Checking…" : "Check Validation Results"}
            </button>
          )}
        </div>
      )}

      {/* Validation results */}
      {validation && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm flex flex-col gap-3">
          <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Validation Results</h4>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Covered</p>
              <p className="mt-1 text-tok-sm font-medium text-[rgb(var(--color-success))]">
                {validation.coverage_summary?.covered_count?.toLocaleString() ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Uncovered</p>
              <p className={`mt-1 text-tok-sm font-medium ${
                (validation.coverage_summary?.uncovered_count ?? 0) > 0
                  ? "text-[rgb(var(--color-danger))]"
                  : "text-[rgb(var(--color-text))]"
              }`}>
                {validation.coverage_summary?.uncovered_count?.toLocaleString() ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Ambiguous</p>
              <p className={`mt-1 text-tok-sm font-medium ${
                (validation.coverage_summary?.ambiguous_count ?? 0) > 0
                  ? "text-[rgb(var(--color-warning))]"
                  : "text-[rgb(var(--color-text))]"
              }`}>
                {validation.coverage_summary?.ambiguous_count?.toLocaleString() ?? "—"}
              </p>
            </div>
          </div>

          {validation.errors?.length > 0 && (
            <div>
              <p className="text-tok-xs font-medium text-[rgb(var(--color-danger))] mb-1">
                Errors ({validation.errors.length})
              </p>
              <ul className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                {validation.errors.map((err, i) => (
                  <li
                    key={i}
                    className="text-tok-xs text-[rgb(var(--color-danger))] bg-[rgb(var(--color-danger))]/10 rounded px-2 py-1"
                  >
                    Row {err.row_number}: [{err.code}] {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Confirm import */}
      {importLog && importLog.load_status === "validating" && (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm flex flex-col gap-4">
          <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Confirm Import</h4>
          <div className="flex flex-col gap-3 max-w-xs">
            <label className={labelClass}>
              Fiscal Period ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                type="number"
                className={inputClass}
                value={fiscalPeriodId}
                onChange={e => setFiscalPeriodId(e.target.value)}
                placeholder="e.g. 11558"
              />
            </label>
            <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))] cursor-pointer">
              <input
                type="checkbox"
                checked={warningsAcknowledged}
                onChange={e => setWarningsAcknowledged(e.target.checked)}
              />
              Acknowledge warnings and proceed
            </label>
            {confirmError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {confirmError}
              </div>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirming || !fiscalPeriodId}
              className={btnPrimary + " self-start"}
            >
              {confirming ? "Confirming…" : "Confirm Import"}
            </button>
          </div>
        </div>
      )}

      {importLog?.load_status === "loaded" && (
        <div className="flex items-center gap-2 text-tok-sm font-medium text-[rgb(var(--color-success))]">
          <span>✓</span>
          <span>Import confirmed. Transaction data loaded and readiness status updated.</span>
        </div>
      )}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function TransactionPlanManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId: fiscalPeriodIdProp, readOnly = false } = {}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  // Pattern 3 (spec_feature_gate_s665.md §2.5) — proactive plan type gate.
  // hasPlanType returns true while loading (optimistic) and true when no bundle
  // assigned (backward compat). Only blocks when bundle exists and key is absent.
  if (!hasPlanType("feature.plan.transaction")) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  // Note: TransactionPlanManager is plan-level, not component-scoped.
  // Manages fiscal period internally. Props accepted for API consistency.
  // planComponentId unused — transaction plans have no component FK in this manager.
  const [activeTab, setActiveTab]       = useState("plan");
  const [fiscalPeriodId, setFiscalPeriodId] = useState(null);

  useEffect(() => {
    async function loadPeriod() {
      try {
        const res = await apiGet("/api/v1/fiscal-periods?period_status=open");
        const period = res.data?.[0];
        if (period) setFiscalPeriodId(period.fiscal_period_id);
      } catch {
        // non-fatal — tabs still render, plan list endpoint is period-optional
      }
    }
    loadPeriod();
  }, []);

  return (
    <section className="flex flex-col gap-6 p-6">

      {/* Header */}
      <header>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 rounded">
            Stage 8A
          </span>
          <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">Transaction Plans</h2>
        </div>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure transaction-level crediting: define plans, set rate matrices, assign to positions, and validate coverage.
        </p>
      </header>

      {/* Tab bar */}
      <div className="flex border-b border-[rgb(var(--color-border))]">
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]"
                : "border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
            }`}
          >
            {tab.label}
            <span className="ml-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {tab.substage}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === "plan" && (
          <PlanDefinitionTab
            fiscalPeriodId={fiscalPeriodId}
            userId={user?.user_id}
            planId={planId}
          />
        )}
        {activeTab === "rates" && <RateMatrixTab />}
        {activeTab === "assignment" && <PositionAssignmentTab />}
        {activeTab === "validation" && <ValidationTab fiscalPeriodId={fiscalPeriodId} />}
      </div>
    </section>
  );
}
